import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { nativeFetch } from '../utils/nativeApi';

function ExportModal({ isOpen, onClose }) {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [loading, setLoading] = useState(false);

  const currencies = ['USD', 'EUR', 'ILS', 'GEL', 'GBP', 'JPY'];

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await nativeFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCurrencyToggle = (currency) => {
    setSelectedCurrencies(prev =>
      prev.includes(currency)
        ? prev.filter(c => c !== currency)
        : [...prev, currency]
    );
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      selectedCategories.forEach(cat => params.append('category', cat));
      selectedCurrencies.forEach(cur => params.append('currency', cur));
      
      if (dateFrom) {
        params.append('date_from', Math.floor(new Date(dateFrom).getTime() / 1000));
      }
      if (dateTo) {
        const endDate = new Date(dateTo);
        endDate.setHours(23, 59, 59);
        params.append('date_to', Math.floor(endDate.getTime() / 1000));
      }
      if (amountMin) params.append('amount_min', amountMin);
      if (amountMax) params.append('amount_max', amountMax);
      params.append('format', exportFormat);

      const response = await nativeFetch(`/api/expenses/export?${params.toString()}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Export downloaded!');
        onClose();
      } else {
        toast.error('Failed to export expenses');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedCategories([]);
    setSelectedCurrencies([]);
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setExportFormat('csv');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black">📤 Export Expenses</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Categories */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Categories</label>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryToggle(cat.id)}
                className={`px-3 py-1 text-sm border-2 border-black rounded-lg font-bold transition-colors ${
                  selectedCategories.includes(cat.id)
                    ? 'bg-primary'
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
          {selectedCategories.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">All categories (none selected)</p>
          )}
        </div>

        {/* Currencies */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Currencies</label>
          <div className="flex flex-wrap gap-2">
            {currencies.map(cur => (
              <button
                key={cur}
                onClick={() => handleCurrencyToggle(cur)}
                className={`px-3 py-1 text-sm border-2 border-black rounded-lg font-bold transition-colors ${
                  selectedCurrencies.includes(cur)
                    ? 'bg-primary'
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                {cur}
              </button>
            ))}
          </div>
          {selectedCurrencies.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">All currencies (none selected)</p>
          )}
        </div>

        {/* Date Range */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Date Range</label>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm"
              placeholder="From"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm"
              placeholder="To"
            />
          </div>
        </div>

        {/* Amount Range (USD) */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Amount Range (USD equivalent)</label>
          <div className="flex gap-2">
            <input
              type="number"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm"
              placeholder="Min"
              min="0"
            />
            <input
              type="number"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm"
              placeholder="Max"
              min="0"
            />
          </div>
        </div>

        {/* Export Format */}
        <div className="mb-6">
          <label className="block text-sm font-bold mb-2">Export Format</label>
          <div className="flex gap-2">
            <button
              onClick={() => setExportFormat('csv')}
              className={`flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold transition-colors ${
                exportFormat === 'csv' ? 'bg-primary' : 'bg-white hover:bg-gray-100'
              }`}
            >
              📄 CSV
            </button>
            <button
              onClick={() => setExportFormat('xlsx')}
              className={`flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold transition-colors ${
                exportFormat === 'xlsx' ? 'bg-primary' : 'bg-white hover:bg-gray-100'
              }`}
            >
              📊 XLSX
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleClear}
            className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
          >
            Clear
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Exporting...' : '📥 Export'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportModal;
